import { Box, Divider, Tooltip } from "@mui/material";
import { density, shape } from "@app/design-tokens";
import { Link, useLocation } from "react-router";

import { useAuth } from "@/auth/authContext";
import { BrandMark } from "@/components/BrandMark";
import { useUiStore } from "@/lib/uiStore";

import { activeSection, NAV_SECTIONS, type NavSection } from "./navSections";

/**
 * 最左邊那條只有圖示的第一層導覽。
 *
 * 固定四節（總覽／服務／通知／個人中心），不隨服務數量成長 —— 理由寫在
 * `navSections.ts`。手機版不顯示這一條，改由 `MobileTabBar` 用同一份宣告
 * 排成底部的懸浮列。
 *
 * 只有圖示的代價是辨識度，所以每一項都有 tooltip，被選中時圖示會從線框
 * 換成實心。
 */

const ITEM = density.railItemSize;

/**
 * 刻意用 `Link` 而不是 `NavLink`。
 *
 * NavLink 會自己決定 `aria-current` —— 它只在「網址與 to 相符」時才加上去，
 * 傳進去的值只是「它自己認定 active 時要用哪個值」。但這裡的 active 判斷是
 * 分節而不是路徑：站在 `/master/items` 時「服務」那一節是目前位置，NavLink
 * 卻認為 `/services` 沒有被選中，於是輔助科技會聽不到任何「目前位置」。
 * 所以 active 由我們算、`aria-current` 由我們給。
 *
 * 點下去會強制展開第二層，覆寫使用者收合的偏好。面板收合之後第二層就不在
 * 畫面上，rail 是唯一還看得到的入口，若點了「服務」仍維持收合，使用者會落在
 * 一個沒有服務導覽的畫面，得再繞去頂列按一次切換 —— 那不是收合的用意。
 *
 * 條件是 `hasPanel` 而不是無條件展開：無條件的話點總覽／通知／個人中心也會
 * 把偏好改掉，之後從總覽的服務磚進到服務時面板會自己冒出來。那個展開不是
 * 使用者點 rail 觸發的，行為會變得無法預期。
 */
function RailItem({ section, active }: { section: NavSection; active: boolean }) {
  const Icon = active ? section.activeIcon : section.icon;
  const setRailExpanded = useUiStore((state) => state.setRailExpanded);

  return (
    <Tooltip title={section.label} placement="right">
      <Box
        component={Link}
        to={section.to}
        onClick={section.hasPanel ? () => setRailExpanded(true) : undefined}
        aria-label={section.label}
        aria-current={active ? "page" : undefined}
        sx={{
          width: ITEM,
          height: ITEM,
          flex: "none",
          borderRadius: `${shape.medium}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: active ? "primary.contrastText" : "text.secondary",
          bgcolor: active ? "primary.main" : "transparent",
          transition: (t) => t.transitions.create(["background-color", "color"]),
          "&:hover": {
            bgcolor: active ? "primary.main" : "action.hover",
            color: active ? "primary.contrastText" : "text.primary",
          },
        }}
      >
        <Icon fontSize="small" />
      </Box>
    </Tooltip>
  );
}

export function ServiceRail() {
  const { me } = useAuth();
  const location = useLocation();
  const serviceCodes = (me?.services ?? []).map((service) => service.code);
  const current = activeSection(location.pathname, serviceCodes);

  // 個人中心沉到最底下：那是「關於我自己」而不是「工作內容」，
  // 跟另外三節不是同一類東西。手機版的底部列也把它放在最右邊。
  const primary = NAV_SECTIONS.filter((section) => section.key !== "account");
  const account = NAV_SECTIONS.filter((section) => section.key === "account");

  return (
    <Box
      component="nav"
      aria-label="主導覽"
      sx={{
        width: density.railWidth,
        flex: "none",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: `${shape.shell}px`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 1.75,
        gap: 0.75,
      }}
    >
      {/* 標誌回總覽。裝飾性的 alt 留空，可點的語意由 aria-label 提供 */}
      <Tooltip title="回總覽" placement="right">
        <Box
          component={Link}
          to="/"
          aria-label="回總覽"
          sx={{ height: 40, display: "flex", alignItems: "center" }}
        >
          <BrandMark size={30} />
        </Box>
      </Tooltip>

      <Divider flexItem sx={{ mx: 2.5, my: 1.25 }} />

      {primary.map((section) => (
        <RailItem key={section.key} section={section} active={current?.key === section.key} />
      ))}

      <Box sx={{ flexGrow: 1 }} />

      {account.map((section) => (
        <RailItem key={section.key} section={section} active={current?.key === section.key} />
      ))}
    </Box>
  );
}
